import type { SupportedLanguage } from '@/utils/translations';
import type { CategoryCode } from '@/types/listings-v2';

const normalize = (v?: string) => (v || '').trim().toLowerCase();

export const localizeFuel = (fuelRaw: string | undefined, lang: SupportedLanguage): string => {
  const v = normalize(fuelRaw);
  // Common synonyms mapping key
  const key = v
    .replace('ă', 'a')
    .replace('â', 'a')
    .replace('î', 'i')
    .replace('ș', 's')
    .replace('ş', 's')
    .replace('ţ', 't')
    .replace('ț', 't');

  const is = (...arr: string[]) => arr.includes(key);

  if (is('benzina', 'benzin', 'petrol')) {
    return lang === 'de' ? 'Benzin' : lang === 'en-GB' ? 'Petrol' : lang === 'fr' ? 'Essence' : 'Benzină';
  }
  if (is('diesel', 'motorina', 'motorină')) {
    return lang === 'de' ? 'Diesel' : lang === 'en-GB' ? 'Diesel' : lang === 'fr' ? 'Diesel' : 'Motorină';
  }
  if (is('hibrid','hybrid')) {
    return lang === 'de' ? 'Hybrid' : lang === 'fr' ? 'Hybride' : lang === 'en-GB' ? 'Hybrid' : 'Hibrid';
  }
  if (is('electric','electrica','electrice')) {
    return lang === 'de' ? 'Elektrisch' : lang === 'fr' ? 'Électrique' : lang === 'en-GB' ? 'Electric' : 'Electrică';
  }
  if (is('gpl','lpg','autogas')) {
    return lang === 'de' ? 'Autogas' : lang === 'en-GB' ? 'LPG' : lang === 'fr' ? 'GPL' : 'GPL';
  }
  if (is('mildhybrid','mild hybrid')) {
    return lang === 'de' ? 'Mild-Hybrid' : lang === 'fr' ? 'Hybride léger' : 'Mild Hybrid';
  }
  return fuelRaw || '';
};

export const localizeTransmission = (txRaw: string | undefined, lang: SupportedLanguage): string => {
  const v = normalize(txRaw)
    .replace('ă', 'a')
    .replace('â', 'a')
    .replace('î', 'i')
    .replace('ș', 's')
    .replace('ş', 's')
    .replace('ţ', 't')
    .replace('ț', 't');

  if (v.includes('manual')) {
    return lang === 'de' ? 'Schaltgetriebe' : lang === 'fr' ? 'Manuelle' : lang === 'en-GB' ? 'Manual' : 'Manuală';
  }
  if (v.includes('automat')) {
    return lang === 'de' ? 'Automatik' : lang === 'fr' ? 'Automatique' : lang === 'en-GB' ? 'Automatic' : 'Automată';
  }
  if (v.includes('semiaut') || v.includes('semi-autom')) {
    return lang === 'de' ? 'Halbautomatik' : lang === 'fr' ? 'Semi-automatique' : 'Semi-automatic';
  }
  if (v.includes('dct') || v.includes('dual') || v.includes('doppelkuppl')) {
    return lang === 'de' ? 'Doppelkupplung' : lang === 'fr' ? 'Double embrayage' : 'Dual-clutch (DCT)';
  }
  if (v.includes('cvt')) return 'CVT';
  if (v.includes('tiptronic')) return 'Tiptronic';
  return txRaw || '';
};

export const localizeCategory = (code: CategoryCode | string | undefined, lang: SupportedLanguage): string => {
  const c = (code || '').toString();
  const map: Record<string, { ro: string; 'en-GB': string; de: string; fr: string }> = {
    cars: { ro: 'Autoturisme', 'en-GB': 'Cars', de: 'Autos', fr: 'Voitures' },
    suv: { ro: 'SUV', 'en-GB': 'SUV', de: 'SUV', fr: 'SUV' },
    van: { ro: 'Autoutilitare', 'en-GB': 'Vans', de: 'Transporter', fr: 'Utilitaires' },
    motorcycle: { ro: 'Motociclete', 'en-GB': 'Motorcycles', de: 'Motorräder', fr: 'Motos' },
    light_truck: { ro: 'Camioane ușoare', 'en-GB': 'Light trucks', de: 'Leichte LKW', fr: 'Camions légers' },
    heavy_truck: { ro: 'Camioane grele', 'en-GB': 'Heavy trucks', de: 'Schwere LKW', fr: 'Poids lourds' },
    bus: { ro: 'Autobuze', 'en-GB': 'Buses', de: 'Busse', fr: 'Bus' },
    tractor: { ro: 'Tractoare', 'en-GB': 'Tractors', de: 'Traktoren', fr: 'Tracteurs' },
    agricultural: { ro: 'Agricole', 'en-GB': 'Agricultural', de: 'Landmaschinen', fr: 'Agricole' },
    construction: { ro: 'Construcții', 'en-GB': 'Construction', de: 'Baumaschinen', fr: 'Chantier' },
    trailer: { ro: 'Remorci', 'en-GB': 'Trailers', de: 'Anhänger', fr: 'Remorques' },
    boat: { ro: 'Ambarcațiuni', 'en-GB': 'Boats', de: 'Boote', fr: 'Bateaux' },
    yacht: { ro: 'Iahturi', 'en-GB': 'Yachts', de: 'Yachten', fr: 'Yachts' },
    jet_ski: { ro: 'Jet Ski', 'en-GB': 'Jet Skis', de: 'Jet-Skis', fr: 'Jet-skis' },
    caravan: { ro: 'Rulote', 'en-GB': 'Caravans', de: 'Wohnwagen', fr: 'Caravanes' },
    camper: { ro: 'Autorulote', 'en-GB': 'Campers', de: 'Wohnmobile', fr: 'Camping-cars' },
    parts: { ro: 'Piese', 'en-GB': 'Parts', de: 'Teile', fr: 'Pièces' },
  };
  const entry = map[c as keyof typeof map];
  if (!entry) return c || '';
  return entry[lang] || entry['en-GB'];
};
